import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, Upload, Download, FileSpreadsheet, Barcode, Camera, Printer, PackagePlus } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { ProductFormDialog } from "@/pages/admin/ProductFormDialog";
import { PiecesDialog } from "@/pages/admin/PiecesDialog";
import { BulkPrintDialog } from "@/pages/admin/BulkPrintDialog";
import { OpeningPiecesImportDialog } from "@/pages/admin/masterbills/OpeningPiecesImportDialog";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatINR, resolveImg, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

export default function Inventory() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rates, setRates] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [piecesProduct, setPiecesProduct] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  // Bulk selection for multi-product barcode printing — a Set of product ids
  // ticked on the Inventory table. Cleared on category/search change so a
  // stale selection never crosses filtered views silently.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPrintOpen, setBulkPrintOpen] = useState(false);
  const [openingImportOpen, setOpeningImportOpen] = useState(false);

  // When the scanner returns a code (QR on the tag, USB scanner, or Enter in the
  // search box), resolve it via the piece scan endpoint and drop the user right
  // into that piece's product (opens the Pieces dialog for the parent product).
  // The search box is populated with the resolved SKU so the table filters too.
  const handleScan = async (code) => {
    setScannerOpen(false);
    const raw = (code || "").trim();
    if (!raw) return;
    try {
      const { data } = await api.get(`/inventory/pieces/scan/${encodeURIComponent(raw)}`);
      const productId = data?.piece?.product_id;
      if (!productId) throw new Error("Piece has no product");
      const sku = data.product?.sku || data.piece?.product_sku || raw;
      setQ(sku);
      // Ensure we have the latest product list (defensive — page load may lag).
      await load();
      const p = products.find((x) => x.id === productId);
      setPiecesProduct(p || { id: productId, name: data.product?.name, sku: data.product?.sku });
      toast.success(`Scanned: ${raw}`);
    } catch (e) {
      // Fallback — maybe they scanned/typed an SKU, not a piece barcode.
      const match = products.find((p) => p.sku?.toLowerCase() === raw.toLowerCase());
      if (match) { setQ(match.sku); setPiecesProduct(match); toast.success(`Found by SKU: ${raw}`); return; }
      setQ(raw);
      toast.error(e.response?.data?.detail || `No piece or SKU found for "${raw}"`);
    }
  };

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };
  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    load();
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/admin/rates").then(({ data }) => setRates(data)).catch(() => {});
    api.get("/admin/diamond-matrix").then(({ data }) => setMatrix(data)).catch(() => setMatrix({ natural: { sizes: [], grades: [], rates: {} }, lab: { sizes: [], grades: [], rates: {} } }));
    // eslint-disable-next-line
  }, []);

  const filtered = products
    .filter((p) => catFilter === "all" || p.category === catFilter)
    .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.sku || "").toLowerCase().includes(q.toLowerCase()));

  const countFor = (catName) => products.filter((p) => catName === "all" || p.category === catName).length;

  // ---- Bulk selection helpers ----
  const toggleOneSelected = (id) => setSelectedIds((prev) => {
    const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const allFilteredSelected = useMemo(
    () => filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id)),
    [filtered, selectedIds]
  );
  const someFilteredSelected = useMemo(
    () => filtered.some((p) => selectedIds.has(p.id)) && !allFilteredSelected,
    [filtered, selectedIds, allFilteredSelected]
  );
  const toggleAllFiltered = () => setSelectedIds((prev) => {
    if (allFilteredSelected) {
      const next = new Set(prev);
      filtered.forEach((p) => next.delete(p.id));
      return next;
    }
    const next = new Set(prev);
    filtered.forEach((p) => next.add(p.id));
    return next;
  });
  const selectedProducts = useMemo(
    () => products.filter((p) => selectedIds.has(p.id)),
    [products, selectedIds]
  );

  const doDelete = async () => {
    try { await api.delete(`/admin/products/${deleteId}`); toast.success("Product deleted"); setDeleteId(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  const downloadTemplate = () => downloadBlob("/admin/products/import-template", "maaheyjewels_import_template.csv").catch(() => toast.error("Download failed"));

  const runImport = async () => {
    if (!importFile) { toast.error("Choose a CSV or Excel file"); return; }
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", importFile);
    try {
      const { data } = await api.post("/admin/products/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported ${data.created} product(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <AdminShell title="Inventory">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) handleScan(q); }}
                 placeholder="Scan QR or enter SKU" className="pl-9 pr-10" />
          <button data-testid="inventory-scan-button" type="button" onClick={() => setScannerOpen(true)}
                  title="Scan QR with camera" aria-label="Scan QR with camera"
                  className="absolute right-1 top-1 h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[#F7F0E6] transition-colors">
            <Camera className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button data-testid="inventory-print-selected-button" variant="secondary" onClick={() => setBulkPrintOpen(true)} className="gap-2">
              <Printer className="h-4 w-4" /> Print Selected Tags <Badge variant="secondary" className="ml-1 bg-white/70">{selectedIds.size}</Badge>
            </Button>
          )}
          <Button data-testid="admin-opening-pieces-button" variant="outline" onClick={() => setOpeningImportOpen(true)} className="gap-2"><PackagePlus className="h-4 w-4" /> Import Opening Pieces</Button>
          <Button data-testid="admin-bulk-import-button" variant="outline" onClick={() => { setImportResult(null); setImportFile(null); setImportOpen(true); }} className="gap-2"><Upload className="h-4 w-4" /> Bulk Import</Button>
          <Button data-testid="admin-inventory-add-button" onClick={() => { setEditing(null); setDialogOpen(true); }} className="gap-2"><Plus className="h-4 w-4" /> Add Product</Button>
        </div>
      </div>

      <Tabs value={catFilter} onValueChange={setCatFilter} className="mb-4">
        <TabsList className="flex-wrap h-auto" data-testid="inventory-category-tabs">
          <TabsTrigger data-testid="inventory-category-all" value="all">All <Badge variant="secondary" className="ml-1.5">{countFor("all")}</Badge></TabsTrigger>
          {categories.map((c) => (
            <TabsTrigger data-testid={`inventory-category-${c.slug}`} key={c.slug} value={c.name}>{c.name} <Badge variant="secondary" className="ml-1.5">{countFor(c.name)}</Badge></TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="admin-inventory-table">
            <TableHeader><TableRow className="bg-[#F4EBDD]">
              <TableHead className="w-10">
                <Checkbox data-testid="inventory-select-all" checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
                          onCheckedChange={toggleAllFiltered} aria-label="Select all products" disabled={filtered.length === 0} />
              </TableHead>
              <TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead>Category</TableHead>
              <TableHead>Gold</TableHead><TableHead>Gold Gross Wt</TableHead><TableHead>Gold Net Wt</TableHead><TableHead>Diamond Wt</TableHead>
              <TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} className={`hover:bg-[#F7F0E6] ${selectedIds.has(p.id) ? "bg-[#FBF3E4]" : ""}`}>
                  <TableCell className="w-10">
                    <Checkbox data-testid="inventory-select-row" checked={selectedIds.has(p.id)} onCheckedChange={() => toggleOneSelected(p.id)} aria-label={`Select ${p.name}`} />
                  </TableCell>
                  <TableCell><div className="flex items-center gap-3"><img src={resolveImg((p.images || [])[0])} alt="" loading="lazy" decoding="async" className="h-10 w-10 rounded object-cover" /><span className="font-medium">{p.name}</span></div></TableCell>
                  <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell className="font-mono text-xs">{p.gold?.karat || "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{p.gold?.gross_weight_g ? `${p.gold.gross_weight_g}g` : (p.gold?.weight_g ? `${p.gold.weight_g}g` : "-")}</TableCell>
                  <TableCell className="font-mono text-xs">{p.gold?.weight_g ? `${p.gold.weight_g}g` : "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{(p.diamonds || []).length > 0 ? `${(p.diamonds.reduce((sum, d) => sum + (Number(d.carat) || 0), 0)).toFixed(2)}ct` : "-"}</TableCell>
                  <TableCell className="font-mono">{formatINR(p.price)}</TableCell>
                  <TableCell data-testid={`inventory-stock-${p.sku}`}>
                    {(p.stock || 0) === 0
                      ? <Badge className="bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8] whitespace-nowrap">Make to Order</Badge>
                      : p.stock <= 3 ? <Badge className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">{p.stock}</Badge> : <span className="font-mono">{p.stock}</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid="inventory-print-tag-button" size="icon" variant="ghost" title="Print all piece tags" onClick={() => window.open(`/admin/print-tags?product_id=${p.id}`, "_blank", "noopener")}><Printer className="h-4 w-4 text-[hsl(var(--primary))]" /></Button>
                    <Button data-testid="inventory-pieces-button" size="icon" variant="ghost" title="Individual Pieces & Barcodes" onClick={() => setPiecesProduct(p)}><Barcode className="h-4 w-4 text-[hsl(var(--primary))]" /></Button>
                    <Button data-testid="inventory-edit-button" size="icon" variant="ghost" onClick={() => { setEditing(p); setDialogOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid="inventory-delete-button" size="icon" variant="ghost" onClick={() => setDeleteId(p.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground">No products found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      {rates && <ProductFormDialog open={dialogOpen} onOpenChange={setDialogOpen} product={editing} categories={categories} rates={rates} matrix={matrix} onSaved={load} />}
      <PiecesDialog open={!!piecesProduct} onOpenChange={(o) => !o && setPiecesProduct(null)} product={piecesProduct} onStockChanged={load} />
      <BulkPrintDialog open={bulkPrintOpen} onOpenChange={setBulkPrintOpen} products={selectedProducts}
                        onPrinted={() => { setSelectedIds(new Set()); setBulkPrintOpen(false); }} />
      <OpeningPiecesImportDialog open={openingImportOpen} onOpenChange={setOpeningImportOpen} onImported={load} />
      <BarcodeScanner open={scannerOpen} onOpenChange={setScannerOpen} onDetected={handleScan} title="Scan QR Tag"
                       description="Point the camera at the QR code on the tag, use a USB scanner, or type a piece code / SKU below." />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="bulk-import-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Bulk Import Products</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-[#F7F0E6] p-4 text-sm">
              <p className="font-medium mb-1">How it works</p>
              <ol className="list-decimal ml-4 space-y-1 text-muted-foreground">
                <li>Download the template (CSV) and fill in your products.</li>
                <li>Category must match an existing category name.</li>
                <li>Diamonds / colour stones format: <span className="font-mono">quality:carat:count</span> (use ; for multiple).</li>
                <li>Images are added later via the product uploader.</li>
                <li>Every design imports at <span className="font-medium">0 stock</span> (Make to Order). Add physical pieces via Master Bills or Import Opening Pieces.</li>
              </ol>
              <Button data-testid="download-template-button" variant="secondary" size="sm" className="gap-1 mt-3" onClick={downloadTemplate}><Download className="h-3.5 w-3.5" /> Download Template</Button>
            </div>
            <div>
              <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-[#F7F0E6] p-6 cursor-pointer">
                <FileSpreadsheet className="h-7 w-7 text-[hsl(var(--primary))]" />
                <span className="text-sm font-medium">{importFile ? importFile.name : "Click to choose CSV / Excel file"}</span>
                <input data-testid="bulk-import-file-input" type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => setImportFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            {importResult && (
              <div data-testid="import-result" className="rounded-lg border border-border p-3 text-sm">
                <div className="text-[#1F5B2E] font-medium">Created {importResult.created} product(s)</div>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-xs text-destructive list-disc ml-4 max-h-32 overflow-y-auto">
                    {importResult.errors.map((er, i) => <li key={`${er}-${i}`}>{er}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
            <Button data-testid="run-import-button" onClick={runImport} disabled={importing || !importFile}>{importing ? "Importing..." : "Import Products"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this product?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-button" onClick={doDelete} className="bg-destructive text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
